import { create } from "zustand";
import { duelsApi, type DuelRoomDetail, type DuelRoomSummary } from "../api/duels";
import { useBattleStore } from "./useBattleStore";
import { useGameStore } from "./useGameStore";

interface DuelState {
  myRoom: DuelRoomDetail | null;
  rooms: DuelRoomSummary[];
  loading: boolean;
  error: string | null;
  expiredModal: string | null;
  pendingBattleInfo: string[];
  lastExpiredKey: string | null;
  battleLaunching: boolean;

  refreshRooms: () => Promise<void>;
  createRoom: () => Promise<void>;
  joinRoom: (roomId: string) => Promise<void>;
  leaveRoom: () => Promise<void>;
  poll: () => Promise<void>;
  dismissExpiredModal: () => void;
  clearMyRoom: () => void;
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export { formatCountdown };

export const useDuelStore = create<DuelState>((set, get) => ({
  myRoom: null,
  rooms: [],
  loading: false,
  error: null,
  expiredModal: null,
  pendingBattleInfo: [],
  lastExpiredKey: null,
  battleLaunching: false,

  refreshRooms: async () => {
    try {
      const res = await duelsApi.listRooms();
      set({ rooms: res.rooms });
    } catch {
      // Non-fatal while polling
    }
  },

  createRoom: async () => {
    set({ loading: true, error: null });
    try {
      const res = await duelsApi.createRoom();
      set({ myRoom: res.room, loading: false });
      await get().refreshRooms();
    } catch (e) {
      set({
        loading: false,
        error: e instanceof Error ? e.message : "Failed to create room",
      });
    }
  },

  joinRoom: async (roomId: string) => {
    set({ loading: true, error: null });
    try {
      const res = await duelsApi.joinRoom(roomId);
      set({
        loading: false,
        pendingBattleInfo: res.info_messages ?? [],
        myRoom: null,
      });
      await useBattleStore.getState().loadBattle(res.battle_id, res.info_messages);
      useGameStore.getState().setScreen("battle");
      void duelsApi.ackStarted();
    } catch (e) {
      set({
        loading: false,
        error: e instanceof Error ? e.message : "Failed to join room",
      });
    }
  },

  leaveRoom: async () => {
    const { myRoom } = get();
    if (!myRoom) return;
    set({ loading: true, error: null });
    try {
      await duelsApi.leaveRoom(myRoom.room_id);
      set({ myRoom: null, loading: false });
      await get().refreshRooms();
    } catch (e) {
      set({
        loading: false,
        error: e instanceof Error ? e.message : "Failed to leave room",
      });
    }
  },

  poll: async () => {
    const { battleLaunching, lastExpiredKey } = get();
    if (battleLaunching) return;

    try {
      const me = await duelsApi.getMe();
      const game = useGameStore.getState();
      const battle = useBattleStore.getState();

      if (me.status === "expired") {
        const key = me.expired_message ?? "expired";
        set({ myRoom: null });
        if (lastExpiredKey === key) return;
        set({ lastExpiredKey: key });

        if (me.info_messages?.length) {
          game.pushInfoMessages(me.info_messages);
        }
        if (game.activeScreen === "duels") {
          return;
        }
        set({ expiredModal: me.expired_message ?? "Duel room closed." });
        return;
      }

      if (me.status === "started" && me.battle_id) {
        if (battle.phase !== "idle" && battle.battle?.id === me.battle_id) {
          return;
        }
        set({
          battleLaunching: true,
          pendingBattleInfo: me.info_messages ?? [],
          myRoom: me.room,
        });
        await useBattleStore.getState().loadBattle(me.battle_id, me.info_messages);
        game.setScreen("battle");
        void duelsApi.ackStarted();
        set({ battleLaunching: false, myRoom: null });
        return;
      }

      if (me.status === "waiting" && me.room) {
        set({ myRoom: me.room, lastExpiredKey: null });
        return;
      }

      set({ myRoom: null });
    } catch {
      // Ignore transient poll errors
    }
  },

  dismissExpiredModal: () => set({ expiredModal: null }),

  clearMyRoom: () => set({ myRoom: null }),
}));
